import type { QuestGroupView, QuestList, QuestView } from "@ec2/contracts";
import type { MessageKey, Translator } from "@ec2/i18n";
import type { QuestCategory as UiCategory, QuestState as UiState } from "@ec2/ui";

export type FilterTab = "all" | "open" | "done";

/** Contract category → @ec2/ui chip category. */
export function uiCategory(category: QuestView["category"]): UiCategory {
  switch (category) {
    case "QR_CODE":
      return "qr";
    case "EXTERNAL_QR":
      return "external";
    case "PHOTO":
      return "photo";
    case "QUIZ":
      return "quiz";
    case "FEEDBACK":
      return "feedback";
    case "PWA_INSTALL":
      return "install";
    case "PUSH_ENABLE":
      return "push";
  }
}

export const CATEGORY_LABEL_KEY: Record<UiCategory, MessageKey> = {
  qr: "ec2.quest.category.qr",
  external: "ec2.quest.category.external",
  photo: "ec2.quest.category.photo",
  quiz: "ec2.quest.category.quiz",
  feedback: "ec2.quest.category.feedback",
  install: "ec2.quest.category.install",
  push: "ec2.quest.category.push",
};

/**
 * Display state: the server's five buckets plus the brief's "locked" preview
 * for active quests whose window hasn't opened yet.
 */
export function uiState(quest: QuestView, now: Date = new Date()): UiState {
  if (quest.state === "active" && quest.startsAt && quest.startsAt.getTime() > now.getTime()) {
    return "locked";
  }
  return quest.state;
}

/** All quests across buckets, in the server's per-bucket order. */
export function flattenQuests(list: QuestList): QuestView[] {
  return [...list.active, ...list.processing, ...list.solved, ...list.failed, ...list.expired];
}

const STATE_ORDER: Record<UiState, number> = {
  active: 0,
  processing: 1,
  locked: 2,
  solved: 3,
  failed: 4,
  expired: 5,
};

/**
 * Brief §6 sort order within a tab: active (deadline asc, none last) →
 * processing → locked → solved → failed → expired.
 */
export function sortQuests(quests: QuestView[], now: Date = new Date()): QuestView[] {
  return [...quests].sort((a, b) => {
    const sa = STATE_ORDER[uiState(a, now)];
    const sb = STATE_ORDER[uiState(b, now)];
    if (sa !== sb) return sa - sb;
    const da = a.endsAt?.getTime() ?? Number.POSITIVE_INFINITY;
    const db = b.endsAt?.getTime() ?? Number.POSITIVE_INFINITY;
    if (da !== db) return da - db;
    return a.sortOrder - b.sortOrder;
  });
}

/**
 * Filter tabs (spec §1 v4): Open = active + processing (+ locked previews);
 * Done = solved + failed + expired; All = everything. Open is the DEFAULT.
 */
export function questsForTab(list: QuestList, tab: FilterTab, now: Date = new Date()): QuestView[] {
  const all = sortQuests(flattenQuests(list), now);
  if (tab === "all") return all;
  const openStates: UiState[] = ["active", "processing", "locked"];
  return all.filter((q) => {
    const open = openStates.includes(uiState(q, now));
    return tab === "open" ? open : !open;
  });
}

/** Count badge for the Done tab (`Done · 3`). */
export function doneCount(list: QuestList): number {
  return list.solved.length + list.failed.length + list.expired.length;
}

// ---------------------------------------------------------------- quest groups

export interface GroupSection {
  group: QuestGroupView;
  quests: QuestView[];
}

/**
 * Concept A sections for one tab: each visible group with its member quests
 * matching the tab (server group order), then the ungrouped rest. A group with
 * no member in the current tab is skipped — except on Open/All, where a group
 * whose remaining members are all hidden keeps its card (count-only row).
 */
export function groupedForTab(
  list: QuestList,
  tab: FilterTab,
  now: Date = new Date(),
): { groups: GroupSection[]; ungrouped: QuestView[] } {
  const all = questsForTab(list, tab, now);
  const groups: GroupSection[] = [];
  const grouped = new Set<string>();
  for (const group of list.groups) {
    const members = all.filter((q) => q.groupId === group.id);
    const keepForHidden = tab !== "done" && group.hiddenUncompletedCount > 0;
    if (members.length === 0 && !keepForHidden) continue;
    for (const member of members) grouped.add(member.id);
    groups.push({ group, quests: members });
  }
  return { groups, ungrouped: all.filter((q) => !grouped.has(q.id)) };
}

/** The caption under the track, per the canonical copy table (docs/07 addendum). */
export function groupBonusCaption(group: QuestGroupView, t: Translator): string | undefined {
  if (group.bonusBucks <= 0) return undefined;
  if (group.bonusAwarded) return t("ec2.group.bonus.unlocked");
  const needed = group.neededCount;
  const hidden = group.hiddenUncompletedCount;
  if (needed >= group.totalQuests) {
    return hidden > 0
      ? t("ec2.group.bonus.allDoneHidden", { total: group.totalQuests, hidden })
      : t("ec2.group.bonus.allDone", { total: group.totalQuests });
  }
  return hidden > 0
    ? t("ec2.group.bonus.atHidden", { needed, total: group.totalQuests, hidden })
    : t("ec2.group.bonus.at", { needed, total: group.totalQuests });
}

/** Photo/quiz/feedback quests aren't solved by scanning — the scan opens the flow. */
export function questFlowPath(quest: QuestView): { to: string; params?: Record<string, string> } {
  switch (quest.category) {
    case "PHOTO":
      return { to: "/photo/$questId", params: { questId: quest.id } };
    case "QUIZ":
      return { to: "/quiz/$questId", params: { questId: quest.id } };
    case "FEEDBACK":
      return { to: "/feedback/$questId", params: { questId: quest.id } };
    default:
      return { to: "/quests/$questId", params: { questId: quest.id } };
  }
}
